<template>
  <basic-page title="Jäsentietolomakkeet" :fluid="true">
    <v-card>
      <v-card-title>
        Jäsentietolomakkeet
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialog = true">
          <v-icon left>mdi-plus</v-icon> Uusi jäsen
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        sort-by="createdAt"
        sort-desc
      >
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template v-slot:item.expiresAt="{ item }">
          <span :class="isExpired(item) ? 'red--text' : ''">
            {{ formatDate(item.expiresAt) }}
          </span>
        </template>
        <template v-slot:item.doneHaka="{ item }">
          <v-btn icon @click="updateStatus(item, 'doneHaka')">
            <v-icon :color="item.doneHaka ? 'success' : 'grey'">
              {{ item.doneHaka ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span v-if="!item.doneHaka" class="grey--text text--lighten-1 ml-1"
              >H</span
            >
          </v-btn>
        </template>
        <template v-slot:item.doneProcountor="{ item }">
          <v-btn icon @click="updateStatus(item, 'doneProcountor')">
            <v-icon :color="item.doneProcountor ? 'success' : 'grey'">
              {{
                item.doneProcountor ? "mdi-check-circle" : "mdi-circle-outline"
              }}
            </v-icon>
            <span
              v-if="!item.doneProcountor"
              class="grey--text text--lighten-1 ml-1"
              >P</span
            >
          </v-btn>
        </template>
        <template v-slot:item.doneOther="{ item }">
          <v-btn icon @click="updateStatus(item, 'doneOther')">
            <v-icon :color="item.doneOther ? 'success' : 'grey'">
              {{ item.doneOther ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span v-if="!item.doneOther" class="grey--text text--lighten-1 ml-1"
              >M</span
            >
          </v-btn>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="mr-2"
                @click="resendLink(item)"
                v-bind="attrs"
                v-on="on"
                :loading="sending === item.id"
              >
                <v-icon>mdi-email-sync</v-icon>
              </v-btn>
            </template>
            <span>Lähetä uusi linkki</span>
          </v-tooltip>

          <v-tooltip bottom v-if="item.status === 'filled'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="mr-2"
                @click="downloadPdf(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Lataa PDF</span>
          </v-tooltip>

          <v-tooltip bottom v-if="item.status === 'filled'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                @click="viewDetails(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Katso tiedot</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                @click="deleteForm(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Poista</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>Lähetä jäsentietolomake</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="newItem.firstName"
              label="Etunimi"
              :rules="[(v) => !!v || 'Vaaditaan']"
            ></v-text-field>
            <v-text-field
              v-model="newItem.lastName"
              label="Sukunimi"
              :rules="[(v) => !!v || 'Vaaditaan']"
            ></v-text-field>
            <v-text-field
              v-model="newItem.email"
              label="Sähköposti (linkin lähetystä varten)"
              :rules="[
                (v) => !!v || 'Vaaditaan',
                (v) => /.+@.+\..+/.test(v) || 'Virheellinen sähköposti',
              ]"
            ></v-text-field>
            <v-select
              v-model="newItem.departments"
              :items="departments"
              label="Osastot"
              multiple
              chips
              :rules="[(v) => v.length > 0 || 'Valitse vähintään yksi osasto']"
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Peruuta</v-btn>
          <v-btn color="primary" @click="createRequest" :loading="creating"
            >Lähetä</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card v-if="selectedItem">
        <v-card-title
          >Lomakkeen tiedot: {{ selectedItem.firstName }}
          {{ selectedItem.lastName }}</v-card-title
        >
        <v-card-text>
          <div v-if="selectedItem && selectedItem.data">
            <h3 class="subtitle-1 font-weight-bold mt-2">1. Jäsentiedot</h3>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td><strong>Nimi:</strong></td>
                    <td>
                      {{ selectedItem.firstName }}
                      {{ selectedItem.lastName }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Syntymäaika:</strong></td>
                    <td>{{ formatDob(selectedItem.data.dob) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Osoite:</strong></td>
                    <td>{{ selectedItem.data.address }}</td>
                  </tr>
                  <tr>
                    <td><strong>Puhelinnumero:</strong></td>
                    <td>{{ selectedItem.data.phone }}</td>
                  </tr>
                  <tr>
                    <td><strong>Sähköposti:</strong></td>
                    <td>{{ selectedItem.data.email }}</td>
                  </tr>
                  <tr>
                    <td><strong>Tilinumero:</strong></td>
                    <td>{{ selectedItem.data.iban }}</td>
                  </tr>
                  <tr>
                    <td><strong>Ruoka-aineallergiat:</strong></td>
                    <td>{{ selectedItem.data.allergies }}</td>
                  </tr>
                  <tr>
                    <td><strong>Ajokorttiluokka:</strong></td>
                    <td>{{ selectedItem.data.drivingLicense }}</td>
                  </tr>
                  <tr>
                    <td><strong>Siviiliammatti:</strong></td>
                    <td>{{ selectedItem.data.civilOccupation }}</td>
                  </tr>
                  <tr>
                    <td><strong>Kuljettajan ammattipätevyys:</strong></td>
                    <td>
                      {{
                        selectedItem.data.professionalDriverQualification
                          ? "Kyllä"
                          : "Ei"
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Osasto:</strong></td>
                    <td>{{ (selectedItem.departments || []).join(", ") }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div v-if="selectedItem.data.guardianName" class="mt-4">
              <h3 class="subtitle-1 font-weight-bold">
                {{
                  isMinor(selectedItem.data.dob)
                    ? "2. Alaikäisen huoltajan tiedot"
                    : "2. Lähiomaisen tiedot"
                }}
              </h3>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>
                        <strong
                          >{{
                            isMinor(selectedItem.data.dob)
                              ? "Huoltajan"
                              : "Lähiomaisen"
                          }}
                          nimi:</strong
                        >
                      </td>
                      <td>{{ selectedItem.data.guardianName }}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong
                          >{{
                            isMinor(selectedItem.data.dob)
                              ? "Huoltajan"
                              : "Lähiomaisen"
                          }}
                          puhelin:</strong
                        >
                      </td>
                      <td>{{ selectedItem.data.guardianPhone }}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong
                          >{{
                            isMinor(selectedItem.data.dob)
                              ? "Huoltajan"
                              : "Lähiomaisen"
                          }}
                          sähköposti:</strong
                        >
                      </td>
                      <td>{{ selectedItem.data.guardianEmail }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <h3 class="subtitle-1 font-weight-bold mt-4">
              3. Suostumukset ja luvat
            </h3>
            <v-list dense>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon
                    :color="
                      selectedItem.data.consentDataProcessing
                        ? 'success'
                        : 'error'
                    "
                    >mdi-check-circle</v-icon
                  >
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title
                    >Henkilötietojen käsittely</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon
                    :color="
                      selectedItem.data.consentImages ? 'success' : 'grey'
                    "
                    >mdi-check-circle</v-icon
                  >
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Kuvien julkaisu</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon>
                  <v-icon
                    :color="
                      selectedItem.data.consentNamePublish ? 'success' : 'grey'
                    "
                    >mdi-check-circle</v-icon
                  >
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title
                    >Nimitietojen julkaisu ilmoitustaululla</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="selectedItem.data.guardianName">
                <v-list-item-icon>
                  <v-icon
                    :color="
                      selectedItem.data.consentGuardian ? 'success' : 'grey'
                    "
                    >mdi-check-circle</v-icon
                  >
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Huoltajan suostumus</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <div class="mt-4">
              <h3 class="subtitle-1 font-weight-bold">4. Lisätiedot</h3>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td><strong>Koulutukset/kelpoisuudet:</strong></td>
                      <td>{{ selectedItem.data.qualifications }}</td>
                    </tr>
                    <tr>
                      <td><strong>Varusteiden kokotiedot:</strong></td>
                      <td>{{ selectedItem.data.sizes }}</td>
                    </tr>
                    <tr>
                      <td><strong>Hälytysryhmät ja profiilit:</strong></td>
                      <td>{{ selectedItem.data.alarmGroups }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
          <div v-if="selectedItem.signatureUrl" class="mt-4">
            <h3 class="subtitle-1 font-weight-bold">Allekirjoitus:</h3>
            <img
              :src="selectedItem.signatureUrl"
              style="max-width: 300px; border: 1px solid #ccc"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false">Sulje</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import { firestore as db } from "@/plugins/firebase.app";
import api from "@/api";
import {
  collection,
  onSnapshot,
  setDoc,
  deleteDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import dayjs from "dayjs";
import { generateMemberFormPDF } from "@/utils/memberFormPdfGenerator";

// Simple random token generator (not crypto secure but sufficient for obscure link if rules are ok)
const generateToken = () => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15) +
    Date.now().toString(36)
  );
};

export default {
  name: "MemberFormsAdmin",
  components: { BasicPage },
  data() {
    return {
      dialog: false,
      detailsDialog: false,
      loading: true,
      creating: false,
      sending: null,
      valid: false,
      selectedItem: null,
      items: [],
      headers: [
        { text: "Nimi", value: "fullName" },
        { text: "Osasto", value: "departmentsStr" },
        { text: "Luotu", value: "createdAt" },
        { text: "Vanhenee", value: "expiresAt" },
        { text: "HAKA", value: "doneHaka" },
        { text: "Procountor", value: "doneProcountor" },
        { text: "Muut", value: "doneOther" },
        { text: "Tila", value: "status" },
        { text: "Toiminnot", value: "actions", sortable: false },
      ],
      newItem: {
        firstName: "",
        lastName: "",
        email: "",
        departments: [],
      },
      departments: [
        "Hälytysosasto",
        "Nuoriso-osasto",
        "Varhaisnuoriso-osasto",
        "Tukiosasto",
        "Veteraaniosasto",
      ],
    };
  },
  computed: {
    processedItems() {
      return this.items.map((i) => ({
        ...i,
        fullName: `${i.firstName} ${i.lastName}`,
        departmentsStr: (i.departments || []).join(", "),
      }));
    },
  },
  mounted() {
    const user = this.$store.state.user;
    if (!user) return;

    const q = query(
      collection(db, "member_forms"),
      where("createdBy", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    this.unsubscribe = onSnapshot(q, (snapshot) => {
      this.items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Helpers for display
        fullName: `${doc.data().firstName} ${doc.data().lastName}`,
        departmentsStr: (doc.data().departments || []).join(", "),
      }));
      this.loading = false;
    });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    formatDate(ts) {
      if (!ts) return "";
      // Handle Firestore Timestamp or ISO string
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return dayjs(d).format("DD.MM.YYYY HH:mm");
    },
    formatDob(dob) {
      if (!dob) return "";
      // Handle potential formats (e.g. Timestamp if stored such, or string)
      const d = dob.toDate ? dob.toDate() : new Date(dob);
      return dayjs(d).format("DD.MM.YYYY");
    },
    isMinor(dob) {
      if (!dob) return false;
      const d = dob.toDate ? dob.toDate() : new Date(dob);
      return dayjs().diff(dayjs(d), "year") < 18;
    },
    isExpired(item) {
      if (!item.expiresAt) return false;
      const d = item.expiresAt.toDate
        ? item.expiresAt.toDate()
        : new Date(item.expiresAt);
      return dayjs().isAfter(dayjs(d));
    },
    getStatusColor(status) {
      if (status === "filled") return "success";
      if (status === "pending") return "warning";
      return "grey";
    },
    getStatusText(status) {
      if (status === "filled") return "Täytetty";
      if (status === "pending") return "Odottaa";
      return status;
    },
    async createRequest() {
      if (!this.$refs.form.validate()) return;
      this.creating = true;

      try {
        await api.createMemberFormInvitation({
          firstName: this.newItem.firstName,
          lastName: this.newItem.lastName,
          email: this.newItem.email,
          departments: this.newItem.departments,
          origin: window.location.origin,
        });

        this.$toast.success("Kutsu ja linkki lähetetty sähköpostitse");
        this.dialog = false;
        this.newItem = {
          firstName: "",
          lastName: "",
          email: "",
          departments: [],
        };
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe: " + e.message);
      } finally {
        this.creating = false;
      }
    },
    async resendLink(item) {
      this.sending = item.id;
      try {
        // Since we are moving to server-generated tokens/docs, resending implies creating a NEW invitation
        // and deleting the old one? Or updating the old one?
        // Server function currently creates a new doc based on input.
        // It's cleaner to just delete old and create new to get a fresh token.

        await deleteDoc(doc(db, "member_forms", item.id)); // Delete old

        await api.createMemberFormInvitation({
          firstName: item.firstName,
          lastName: item.lastName,
          email: item.email,
          departments: item.departments,
          origin: window.location.origin,
        });

        this.$toast.success("Uusi linkki lähetetty");
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe uudelleenlähetyksessä");
      } finally {
        this.sending = null;
      }
    },
    async downloadPdf(item) {
      try {
        const doc = await generateMemberFormPDF(item);
        doc.save(`jasentietolomake_${item.lastName}_${item.firstName}.pdf`);
      } catch (e) {
        console.error(e);
        this.$toast.error("PDF luonti epäonnistui");
      }
    },
    viewDetails(item) {
      this.selectedItem = item;
      this.detailsDialog = true;
    },

    async deleteForm(item) {
      const confirm = await this.$deleteConfirm(
        "Haluatko varmasti poistaa tämän lomakkeen?",
      );
      if (!confirm) return;

      try {
        await deleteDoc(doc(db, "member_forms", item.id));
        this.$toast.success("Lomake poistettu");
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe poistettaessa: " + e.message);
      }
    },
    async updateStatus(item, field) {
      try {
        await updateDoc(doc(db, "member_forms", item.id), {
          [field]: !item[field],
        });
        this.$toast.success("Tila päivitetty");
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe tilan päivityksessä");
      }
    },

    // Removed sendEmail as it is handled by backend now
  },
};
</script>
